import { useCallback, useMemo } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { CareerQuestion, CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { AspirationCategory } from '@backrs_ui/grow-ui/src/pages/Result/hooks/useAspirationCategories';

import { Box, Collapse, Typography, SxProps, Theme } from '@mui/material';

import { TradeoffAnswerPills } from './TradeoffAnswerPills';

type AspirationCategoryItemProps = Readonly<{
  category: AspirationCategory;
  isExpanded: boolean;
  onToggle: () => void;
  draftResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
  onAnswerChange: (questionId: number, optionIds: number[]) => void;
  registerRef?: (key: string, el: HTMLElement | null) => void;
  onHeaderHoverChange?: (key: string | null) => void;
}>;

export function AspirationCategoryItem({
  category,
  isExpanded,
  onToggle,
  draftResponseSet,
  careerQuestions,
  onAnswerChange,
  registerRef,
  onHeaderHoverChange,
}: AspirationCategoryItemProps) {
  const selectedCount = useMemo(() => {
    return category.questions.reduce((total, question) => {
      const response = draftResponseSet.user_responses.find(
        (r) => r.career_question_id === question.id,
      );

      return total + (response?.career_question_option_ids?.length ?? 0);
    }, 0);
  }, [category.questions, draftResponseSet.user_responses]);

  const containerRefCallback = useCallback(
    (el: HTMLElement | null) => {
      registerRef?.(category.key, el);
    },
    [registerRef, category.key],
  );

  return (
    <Box ref={containerRefCallback} sx={containerSx(isExpanded)}>
      <Box
        id={`tradeoff-category-${category.key}`}
        sx={headerSx}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        onMouseEnter={() => onHeaderHoverChange?.(category.key)}
        onMouseLeave={() => onHeaderHoverChange?.(null)}
        onFocus={() => onHeaderHoverChange?.(category.key)}
        onBlur={() => onHeaderHoverChange?.(null)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`Toggle ${category.name} (${selectedCount} selected)`}
      >
        <Box sx={headerLeftSx}>
          <AppIcon iconName={category.iconName} diameter="20px" color="currentColor" />
          <Typography variant="text-sm-semibold" sx={titleSx}>
            {category.name}
          </Typography>
        </Box>
        <Box sx={countBadgeSx(isExpanded)}>
          <Typography variant="text-xs-semibold" sx={countBadgeTextSx(isExpanded)}>
            {selectedCount}
          </Typography>
        </Box>
      </Box>

      <Collapse
        in={isExpanded}
        timeout={300}
        easing={{ enter: 'ease-in-out', exit: 'ease-in-out' }}
      >
        <Box sx={questionListSx}>
          {category.questions.map((question) => {
            const response = draftResponseSet.user_responses.find(
              (r) => r.career_question_id === question.id,
            );

            return (
              <Box key={question.id} sx={questionSx}>
                <Typography variant="text-sm-medium" sx={questionTitleSx}>
                  {question.aspirational_title || question.title}
                </Typography>
                <TradeoffAnswerPills
                  question={question}
                  response={response}
                  draftResponseSet={draftResponseSet}
                  careerQuestions={careerQuestions}
                  onSelect={onAnswerChange}
                />
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

const containerSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors[isExpanded ? 'Brand/300' : 'Gray/500']}`,
    backgroundColor: backrs.colors['Colors/Base/White'],
    transition: 'border-color 0.2s ease-in-out',
    overflow: 'hidden',
    '&:hover, &:focus-within': {
      borderColor: backrs.colors['Brand/500'],
    },
  });

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
  color: backrs.colors['Colors/Text/text-primary'],
  cursor: 'pointer',
  outline: 'none',
  userSelect: 'none',
});

const headerLeftSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = {
  color: 'inherit',
};

const countBadgeSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    borderRadius: backrs.radius['radius-full'],
    border: `1px solid ${backrs.colors[isExpanded ? 'Brand/300' : 'Gray/200']}`,
    backgroundColor: isExpanded ? backrs.colors['Brand/50'] : backrs.colors['Gray/100'],
    padding: `0 ${backrs.spacing['spacing-xs']}`,
    transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
  });

const countBadgeTextSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: isExpanded ? backrs.colors['Brand/700'] : backrs.colors['Colors/Text/text-secondary'],
  });

const questionListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  padding: `0 ${backrs.spacing['spacing-xl']} ${backrs.spacing['spacing-xl']}`,
});

const questionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const questionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
